// SPDX-License-Identifier: GPL-3.0-or-later
// Text cues for the feedback events that the engine text path does not cover.
//
// THEME rule: audio is optional, and each meaningful audio cue needs a text cue. The HUD, the
// callout box and the live region already say what the engine reports (a ball, a mode, a score).
// Two events come from the page and not from the engine: the start of a featured turn and a new
// best score. This module writes their text. The text is plain text: the page sets it with
// `textContent`. A name is cut to a short length and has no markup meaning.
//
// The boss has three meaningful sounds: it starts, it changes phase and it is defeated. Four table
// moments have a meaningful sound and no other text: a bank is complete, an extra ball, a hurry-up
// collect and a run clear. Play is calm, so the page shows no popup box for any of them. A screen
// reader hears them in the live region. The HUD boss bar is the visible cue for the boss.

import type { FeedbackBus, FeedbackEvent } from "../src/shared/feedback/index.ts";
import { formatPoints } from "../src/table/callouts.ts";
import type { BestResult } from "./high-score.ts";
import type { LiveFeed } from "./hud.ts";

/** A name is cut to this many characters. */
export const NAME_MAX_CHARS = 24;

/** A name as plain short text. A name that is missing, not text or blank gives an empty string. */
export function cleanName(name: unknown): string {
  if (typeof name !== "string") return "";
  return Array.from(name.trim()).slice(0, NAME_MAX_CHARS).join("");
}

/** The text for the callout box when a featured turn starts. */
export function featuredTurnCallout(name?: string): string {
  const who = cleanName(name);
  return who === "" ? "FEATURED TURN" : `FEATURED TURN: ${who}`;
}

/** The sentence for the live region when a featured turn starts. */
export function featuredTurnLive(name?: string): string {
  const who = cleanName(name);
  return who === "" ? "Featured turn started." : `Featured turn started: ${who}.`;
}

/** The line of the game over dialog for the best score. Empty when the player has no best yet. */
export function bestLine(result: BestResult): string {
  if (result.best <= 0) return "";
  return result.isNew
    ? `NEW BEST SCORE ${formatPoints(result.best)}`
    : `BEST SCORE ${formatPoints(result.best)}`;
}

/** The sentence for the live region when a new best score is set. Null when there is none. */
export function newBestLive(result: BestResult): string | null {
  return result.isNew ? `New best score. ${formatPoints(result.best)}.` : null;
}

/** The sentence for the live region when a boss starts. */
export function bossStartLive(name: unknown, stage: number): string {
  const who = cleanName(name);
  const where = Number.isFinite(stage) ? ` of stage ${Math.floor(stage)}` : "";
  return who === "" ? `Boss${where} appears.` : `Boss${where}: ${who} appears.`;
}

/** The share of the boss health that is left, as a whole percent from 0 to 100. */
export function bossHealthPercent(hp: number, maxHp: number): number {
  if (!Number.isFinite(hp) || !Number.isFinite(maxHp) || maxHp <= 0) return 0;
  return Math.round(Math.min(1, Math.max(0, hp / maxHp)) * 100);
}

/** The sentence for the live region when a boss changes phase. */
export function bossPhaseLive(phase: number, hp: number, maxHp: number): string {
  const n = Number.isFinite(phase) ? Math.floor(phase) : 0;
  return `Boss phase ${n}. ${bossHealthPercent(hp, maxHp)} percent health left.`;
}

/** The sentence for the live region when a boss is defeated. */
export function bossDefeatLive(name: unknown): string {
  const who = cleanName(name);
  return who === "" ? "Boss defeated." : `Boss defeated: ${who}.`;
}

/** The live region sentence of a boss feedback event, or null for any other event. */
export function bossLiveLine(ev: FeedbackEvent): string | null {
  switch (ev.kind) {
    case "boss_start":
      return bossStartLive(ev.name, ev.stage);
    case "boss_phase":
      return bossPhaseLive(ev.phase, ev.hp, ev.maxHp);
    case "boss_defeat":
      return bossDefeatLive(ev.name);
    default:
      return null;
  }
}

/** The live region sentence when a drop target bank is complete. The label is optional. */
export function bankCompleteLive(label: unknown): string {
  const who = cleanName(label);
  return who === "" ? "Bank complete." : `${who} complete.`;
}

/** The live region sentence when the player collects a hurry-up. The value is optional. */
export function hurryUpCollectLive(points: unknown): string {
  if (typeof points !== "number" || !Number.isFinite(points)) return "Hurry-up collected.";
  return `Hurry-up collected. ${formatPoints(points)}.`;
}

/** The live region sentence of a table moment that has a sound and no other text, else null. */
export function momentLiveLine(ev: FeedbackEvent): string | null {
  switch (ev.kind) {
    case "drop_bank_complete":
      return bankCompleteLive(ev.label);
    case "extra_ball":
      return "Extra ball.";
    case "hurry_up_collect":
      return hurryUpCollectLive(ev.scoreDelta);
    case "run_clear":
      return "Run clear.";
    default:
      return null;
  }
}

/** The live region sentence of a boss event or a table moment, or null for any other event. */
export function liveLineOf(ev: FeedbackEvent): string | null {
  return bossLiveLine(ev) ?? momentLiveLine(ev);
}

/**
 * Send the live text of each feedback event to the live feed at high priority. A boss event or a
 * table moment has a line. Other events are ignored. A null line is not sent. The result removes
 * the listener. Calling it again does nothing.
 */
export function routeLiveText(
  bus: Pick<FeedbackBus, "subscribe">,
  feed: Pick<LiveFeed, "push">,
): () => void {
  return bus.subscribe((ev) => {
    const line = liveLineOf(ev);
    if (line !== null) feed.push(line, "high");
  });
}
